import { isBitrise, type Assertion, type ProfileFormValues, type ProjectEnvironment } from './profileFormModel';
import { submitPayloadOf } from './bindingSubmitModel';

/** Builds the request body of a build profile from the form, and maps API failures back to the form's fields. */

const trimmed = (value: string): string => value.trim();

/** `verification`: size budget, bundle id, version monotonic (always sent), signing and the cleaned env assertions. */
export function buildVerification(values: ProfileFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (values.sizeBudgetMb) body.sizeBudgetMb = Number(values.sizeBudgetMb);
  if (trimmed(values.bundleId)) body.bundleId = trimmed(values.bundleId);
  body.versionMonotonic = values.versionMonotonic;
  if (values.signingCheck) body.signing = 'store_app';
  const clean = values.assertions.filter((a) => a.key.trim());
  if (clean.length) body.envAssertions = clean.map(assertionBody);
  return body;
}

const assertionBody = (a: Assertion): { key: string; pattern?: string } => ({ key: a.key.trim(), ...(a.pattern.trim() ? { pattern: a.pattern.trim() } : {}) });

/** The environments whose binding is switched on, in the order they are shown. */
export const boundEnvironments = (values: ProfileFormValues, environments: ProjectEnvironment[]): ProjectEnvironment[] => environments.filter((env) => values.bindings[env.id]?.on);

/**
 * EAS: the eas.json profile (+ one per binding); Bitrise: the workflow per environment slug, as hangar.yml has it, and
 * the stack instead of the resource class.
 */
export function buildProviderConfig(values: ProfileFormValues, bound: ProjectEnvironment[], bitrise: boolean): Record<string, unknown> {
  const resource = trimmed(values.resourceClass);
  if (bitrise) {
    return { workflowByEnvironment: Object.fromEntries(bound.map((env) => [env.slug, values.bindings[env.id]!.profile.trim()])), ...(resource ? { stack: resource } : {}) };
  }
  return { ...(trimmed(values.providerProfile) ? { profile: trimmed(values.providerProfile) } : {}), ...(resource ? { resourceClass: resource } : {}) };
}

export interface BindingBody {
  environmentId: string;
  providerConfig?: { profile: string };
  defaultStoreAppId?: string;
  submit?: ReturnType<typeof submitPayloadOf>;
}

/**
 * One entry per bound environment; EAS bindings carry their own profile, Bitrise ones are in `providerConfig`. The store
 * app and the submit settings travel with the binding, so saving the form never drops them.
 */
export function buildBindings(values: ProfileFormValues, bound: ProjectEnvironment[], bitrise: boolean): BindingBody[] {
  return bound.map((env) => {
    const b = values.bindings[env.id];
    const profile = b?.profile.trim();
    const storeAppId = b?.storeAppId;
    return {
      environmentId: env.id,
      ...(!bitrise && profile ? { providerConfig: { profile } } : {}),
      ...(storeAppId ? { defaultStoreAppId: storeAppId } : {}),
      ...(storeAppId && b?.submit ? { submit: submitPayloadOf(b.submit) } : {}),
    };
  });
}

/** The launch form is sent only when there is some JSON (already validated). */
export const buildLaunchForm = (json: string): { launchForm?: Record<string, unknown> } => (json.trim() ? { launchForm: JSON.parse(json) as Record<string, unknown> } : {});

export interface ProfileBody {
  name: string;
  providerAccountId: string;
  executionModes: ProfileFormValues['modes'];
  providerConfig: Record<string, unknown>;
  artifactKind: ProfileFormValues['artifactKind'];
  launchForm?: Record<string, unknown>;
  verification: Record<string, unknown>;
  timeoutMinutes: number;
  environments: BindingBody[];
}

export interface PayloadContext {
  accountKind: string | undefined;
  allowedModes: ProfileFormValues['modes'];
  environments: ProjectEnvironment[];
}

/** The body shared by create (plus `slug` and `platform`) and update. Only call it on a form that validated. */
export function buildProfileBody(values: ProfileFormValues, ctx: PayloadContext): ProfileBody {
  const bitrise = isBitrise(ctx.accountKind);
  const bound = boundEnvironments(values, ctx.environments);
  return {
    name: values.name.trim(),
    providerAccountId: values.providerAccountId,
    executionModes: values.modes.filter((m) => ctx.allowedModes.includes(m)),
    providerConfig: buildProviderConfig(values, bound, bitrise),
    artifactKind: values.artifactKind,
    ...buildLaunchForm(values.launchFormJson),
    verification: buildVerification(values),
    timeoutMinutes: Number(values.timeout),
    environments: buildBindings(values, bound, bitrise),
  };
}

/** Where the API reports a problem: a JSON pointer and its message. */
export interface PointerIssue {
  pointer: string;
  message: string;
}

/** The error-carrying part of an `ApiError`, so this module needs no runtime import of the client. */
export interface PointerSource {
  errors: Array<{ pointer: string; message: string }>;
  details?: unknown;
}

/** Pointers from `errors` plus the zod-style `details: [{ path, message }]` some 422s carry. */
export function pointerIssuesOf(err: PointerSource): PointerIssue[] {
  const details = Array.isArray(err.details) ? (err.details as Array<{ path?: unknown; message?: unknown }>) : [];
  return [
    ...err.errors.map((e) => ({ pointer: e.pointer, message: e.message })),
    ...details.filter((d) => Array.isArray(d.path)).map((d) => ({ pointer: `/${(d.path as unknown[]).join('/')}`, message: String(d.message ?? '') })),
  ];
}

/** The field key one pointer maps to (`binding:<envId>` or `providerProfile`), if it is one the form shows by name. */
function fieldOfIssue(pointer: string, sentEnvIds: string[], envIdOfSlug: (slug: string) => string | undefined): string | undefined {
  const binding = /^\/environments\/(\d+)\/providerConfig\/profile$/.exec(pointer);
  const workflow = /^\/providerConfig\/workflowByEnvironment\/([^/]+)$/.exec(pointer);
  const envId = binding ? sentEnvIds[Number(binding[1])] : workflow ? envIdOfSlug(workflow[1]!) : undefined;
  if (envId) return `binding:${envId}`;
  return pointer === '/providerConfig/profile' ? 'providerProfile' : undefined;
}

/**
 * 422 pointers the API sends for EAS profile names and Bitrise workflows (`/providerConfig/profile`,
 * `/environments/N/providerConfig/profile`, `/providerConfig/workflowByEnvironment/<env>`) → the field that holds
 * them. `N` indexes the bindings that were sent, so it maps back through `sentEnvIds`; `<env>` is a slug. The first
 * message for a field wins.
 */
export function profileNameErrors(err: PointerSource, sentEnvIds: string[], envIdOfSlug: (slug: string) => string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { pointer, message } of pointerIssuesOf(err)) {
    const key = fieldOfIssue(pointer, sentEnvIds, envIdOfSlug);
    if (key) out[key] ??= message;
  }
  return out;
}

/** Server field errors + the named ones; a named error replaces the generic `providerConfig` / `environments` one it covers. */
export function mergeServerErrors(fields: Record<string, string>, named: Record<string, string>): Record<string, string> {
  const merged = { ...fields, ...named };
  if (named.providerProfile) delete merged.providerConfig;
  if (Object.keys(named).some((k) => k.startsWith('binding:'))) delete merged.environments;
  return merged;
}
